import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ageInDays,
  BRAND_ART_PRESETS,
  DEFAULT_BRAND_ART_MODEL,
  formatDateTime,
  platformRowsToCsv,
  summarizePlatform,
  tenantStage,
  type PlatformOverview,
} from '@icetus/contracts';
import type {
  BrandImageConfigView,
  TranslationProviderView,
  UpdateBrandImageConfigInput,
  UpdateTranslationProviderInput,
} from '@icetus/contracts';
import { Badge, Button, Card } from '@icetus/ui';
import {
  ApiError,
  fetchBrandImageConfig,
  fetchPlatformOverview,
  fetchTranslationProvider,
  saveBrandImageConfig,
  saveTranslationProvider,
  testBrandImageConfig,
  testTranslationProvider,
} from '../api';
import { downloadCsv } from '../lib/download';
import { PLATFORM_FILTERS, matchesPlatformFilter, type PlatformFilter } from '../lib/platform-view';

/**
 * 平台总览（仅超管）。
 *
 * 这一页回答的是四个具体的运营问题，不是"给个仪表盘看看"：
 *   1) 现在有几个客户，各自什么时候注册的 —— 谁刚进来还没人管
 *   2) 每个客户几个站点、几个已发布 —— 「已开通」不等于「已上线」，
 *      默认页是草稿，开通了但没发布才是最常见的沉默流失
 *   3) 付费情况：试用 / 付费 / 欠费 / 无订阅记录，以及累计收了多少
 *   4) 下周谁会掉：7 天内到期、欠费、租户被停
 *
 * 三个刻意的取舍：
 *
 *   - **不做分页**。这是"全平台有多少客户"的表，分页会让汇总变成
 *     "当前页的小计"，而超管要的恰恰是全量。"需要跟进"这个筛选
 *     承担了"只看该打电话的人"的职责。
 *   - **绝对时间，不写"3 天前"**。报表要能跟客户自己的话对上
 *     （"我上个月 28 号注册的"），相对时间在导出之后就废了。
 *   - **金额不在这里算**。每月收入由后端按计费口径折算（年付均摊、
 *     试用不计），前端只做加法。两处各算一遍，迟早对不上账单页。
 */

const yuan = (cents: number): string =>
  `¥${(cents / 100).toLocaleString('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const FILTERS = PLATFORM_FILTERS;

/**
 * 下载 CSV（实现见 `lib/download`，询盘页也用它）。
 */
export function PlatformPage() {
  const [data, setData] = useState<PlatformOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState('');
  const [filter, setFilter] = useState<PlatformFilter>('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await fetchPlatformOverview());
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setError('这份报表只有平台超管能看。');
      } else if (err instanceof ApiError && err.status === 503) {
        setError(
          '服务端没有配置 DATABASE_ADMIN_URL，读不到跨租户的名册。这是部署问题，需要运维补上这项配置。',
        );
      } else {
        setError(err instanceof ApiError ? err.message : '报表加载失败');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * "现在"只在数据到手时取一次。
   *
   * 每次渲染都 `new Date()` 会让剩余天数在输入框敲字时跳动 ——
   * 那不是"实时"，那是抖。
   */
  const now = useMemo(() => {
    return data ? new Date(data.generatedAt) : new Date();
  }, [data]);

  const summary = useMemo(() => (data ? summarizePlatform(data.items, now) : null), [data, now]);

  const rows = useMemo(
    () => (data ? data.items.filter((row) => matchesPlatformFilter(row, filter, keyword, now)) : []),
    [data, filter, keyword, now],
  );

  const exportCsv = () => {
    if (!data) return;
    // 导出的是**筛选后的**行：超管筛出"需要跟进"再导出，正是要给同事分派任务
    const stamp = formatDateTime(new Date().toISOString()).replaceAll(/[: ]/g, '-');
    downloadCsv(`平台总览-${stamp}.csv`, platformRowsToCsv(rows, now));
  };

  return (
    <section className="stack">
    <Card
      title="平台总览"
      actions={
        <div className="platform-actions">
          <Button variant="ghost" disabled={loading} onClick={() => void load()}>
            {loading ? '读取中…' : '重新读取'}
          </Button>
          <Button variant="primary" disabled={!data || rows.length === 0} onClick={exportCsv}>
            导出 CSV
          </Button>
        </div>
      }
    >
      {error ? <p className="error">{error}</p> : null}
      {loading && !data ? <p className="muted">加载中…</p> : null}

      {summary && data ? (
        <>
          <div className="platform-kpis">
            <div className="kpi">
              <span className="kpi__label">租户</span>
              <strong className="kpi__value">{summary.tenantCount}</strong>
              <span className="kpi__hint">
                近 30 天新增 {summary.newLast30Days}
                {summary.activeTenantCount < summary.tenantCount
                  ? ` · ${summary.tenantCount - summary.activeTenantCount} 个已停用`
                  : ''}
              </span>
            </div>
            <div className="kpi">
              <span className="kpi__label">站点</span>
              <strong className="kpi__value">{summary.siteCount}</strong>
              <span className="kpi__hint">
                {summary.liveSiteCount} 个对外在线 · {summary.domainCount} 个域名已生效
              </span>
            </div>
            <div className="kpi">
              <span className="kpi__label">月度收入</span>
              <strong className="kpi__value">{yuan(summary.mrrCents)}</strong>
              <span className="kpi__hint">年付按 12 个月均摊 · 累计实付 {yuan(summary.paidTotalCents)}</span>
            </div>
            <div className="kpi">
              <span className="kpi__label">试用中</span>
              <strong className="kpi__value">{summary.trialingCount}</strong>
              <span className="kpi__hint">{summary.dueSoonCount} 个 7 天内到期</span>
            </div>
            <div className="kpi">
              <span className="kpi__label">付费中</span>
              <strong className="kpi__value">{summary.payingCount}</strong>
              <span className="kpi__hint">
                {summary.pastDueCount > 0 ? `${summary.pastDueCount} 个欠费` : '没有欠费'}
              </span>
            </div>
            <div className={`kpi${summary.needsAttentionCount > 0 ? ' kpi--warn' : ''}`}>
              <span className="kpi__label">需要跟进</span>
              <strong className="kpi__value">{summary.needsAttentionCount}</strong>
              <span className="kpi__hint">
                {summary.noSubscriptionCount > 0
                  ? `其中 ${summary.noSubscriptionCount} 个没有订阅记录`
                  : '临期、欠费或已停用的客户'}
              </span>
            </div>
          </div>

          {/**
           * 这行人最容易漏：他们注册成功但不在任何租户里，
           * 所以「租户总数」根本不包含他们。开通失败一次的人就长这样。
           */}
          {data.orphanUserCount > 0 ? (
            <div className="notice notice--warn">
              <span>
                另有 <strong>{data.orphanUserCount}</strong> 个账号注册后没有加入任何站点
                （开通没走完，或者已被移出成员）。他们不在下面的表里，但每一个都可能是
                「以为自己在用、其实没有站」的客户。
              </span>
            </div>
          ) : null}

          <div className="platform-toolbar">
            <input
              className="field"
              type="search"
              placeholder="搜租户名、站点标识或所有者邮箱"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
            <div className="platform-filters" role="tablist">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  role="tab"
                  aria-selected={filter === f.key}
                  className={filter === f.key ? 'pill pill--on' : 'pill'}
                  onClick={() => setFilter(f.key)}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {rows.length === 0 ? (
            <p className="muted platform-empty">
              {data.items.length === 0
                ? '还没有任何租户注册。第一个客户注册并开通之后，这里会出现他。'
                : '当前筛选条件下没有租户。'}
            </p>
          ) : (
            <div className="tablewrap">
              <table className="table platform-table">
                <thead>
                  <tr>
                    <th>租户</th>
                    <th>注册</th>
                    <th>套餐</th>
                    <th>状态</th>
                    <th>站点</th>
                    <th>内容</th>
                    <th>收款</th>
                    <th>最后活跃</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const view = tenantStage(row, now);
                    const age = ageInDays(row.createdAt, now);
                    return (
                      <tr key={row.id} className={view.needsAttention ? 'is-attention' : undefined}>
                        <td>
                          <div className="platform-tenant">{row.name}</div>
                          <div className="muted platform-sub">
                            <code>{row.slug}</code>
                            <span>{row.ownerEmail ?? '没有所有者'}</span>
                          </div>
                        </td>
                        <td className="platform-nowrap">
                          <div>{formatDateTime(row.createdAt).slice(0, 10)}</div>
                          <div className="muted platform-sub">
                            {age === null ? '—' : age === 0 ? '今天' : `${age} 天`}
                          </div>
                        </td>
                        <td className="platform-nowrap">
                          <div>{row.planName}</div>
                          <div className="muted platform-sub">
                            {row.subCycle === 'annual' ? '年付' : row.subCycle === 'monthly' ? '月付' : '—'}
                          </div>
                        </td>
                        <td className="platform-nowrap">
                          <Badge tone={view.tone}>{view.label}</Badge>
                          {row.subPeriodEnd ? (
                            <div className="muted platform-sub">
                              {formatDateTime(row.subPeriodEnd).slice(0, 10)} 到期
                            </div>
                          ) : null}
                        </td>
                        <td className="platform-nowrap">
                          <div>
                            {row.siteCount} 个（在线 {row.liveSiteCount}）
                          </div>
                          <div className="muted platform-sub">域名 {row.domainCount}</div>
                        </td>
                        <td className="platform-nowrap">
                          <div>
                            页面 {row.publishedPages} · 商品 {row.activeProducts}
                          </div>
                          <div className="muted platform-sub">
                            成员 {row.memberCount}
                            {row.themeSubCount > 0 ? ` · 付费主题 ${row.themeSubCount}` : ''}
                          </div>
                        </td>
                        <td className="platform-nowrap">
                          <div>{yuan(row.paidCents)}</div>
                          <div className="muted platform-sub">
                            {row.lastPaidAt
                              ? `最近 ${formatDateTime(row.lastPaidAt).slice(0, 10)}`
                              : '还没收到过付款'}
                          </div>
                        </td>
                        <td className="muted platform-nowrap">
                          {row.lastActivityAt ? formatDateTime(row.lastActivityAt) : '没有操作记录'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <p className="muted platform-meta">
            共 {rows.length} 行（全平台 {data.items.length} 个租户） · 数据读取于{' '}
            {formatDateTime(data.generatedAt)} · 月度收入按订阅折算，与「套餐与账单」页同口径
          </p>
        </>
      ) : null}
    </Card>

    {/* 翻译模型是平台级配置（一份 key 服务所有租户），所以挂在超管这一页 */}
    <TranslationProviderCard />
    {/* 图像模型同理：一次配置服务全平台，换模型不该走一次发布 */}
    <BrandImageProviderCard />
    </section>
  );
}

/**
 * 翻译模型配置（仅平台超管）。
 *
 * ## 为什么这一个卡值得单独做
 *
 * 客户那边点「开始翻译」花的是**我们的**账单。换模型、换 key、调批量大小
 * 都不应该走一次代码发布 —— 尤其是模型价格与可用性都在变，一次事故
 * 需要能在一分钟内切到备用模型。
 *
 * ## 几个刻意的设计
 *
 * · **key 只回显末四位**。服务端存的是 AES-256-GCM 密文，前端拿不到明文；
 *   输入框留空表示"不改"，填了才覆盖 —— 否则每次保存都要重新贴一遍 key。
 * · **先测试连通性再保存不是流程要求**。测试用的是**已保存**的配置，所以
 *   改完必须先保存才能测出新配置通不通。这一点写在界面上，不让人猜。
 * · **temperature 上限 1.0**。翻译要确定性，高于 1 只会让同一段文字每次翻出
 *   不同的东西，而客户校对过的那一版会变得不可复现。
 */
function TranslationProviderCard() {
  const [view, setView] = useState<TranslationProviderView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  /** 表单草稿。apiKey 单独一格：它是一次性的，不回填 */
  const [form, setForm] = useState<UpdateTranslationProviderInput>({});
  const [apiKey, setApiKey] = useState('');

  const load = useCallback(async () => {
    try {
      const next = await fetchTranslationProvider();
      setView(next);
      setForm({
        provider: next.provider,
        baseUrl: next.baseUrl,
        model: next.model,
        batchSize: next.batchSize,
        temperature: next.temperature,
      });
      setError(null);
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 403
          ? '这个配置只有平台超管能改。'
          : err instanceof ApiError
            ? err.message
            : '读取翻译配置失败',
      );
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      /**
       * 只有真的填了 key 才带上它。
       *
       * 传空串的语义是"清除 key"（契约里写明了），而这里留空的语义是
       * "不改" —— 两者必须分开，否则超管改一下模型名就会把 key 清掉，
       * 下一次翻译全线报"没有配置模型"。
       */
      const input: UpdateTranslationProviderInput = { ...form };
      if (apiKey.trim() !== '') input.apiKey = apiKey.trim();
      const next = await saveTranslationProvider(input);
      setView(next);
      setApiKey('');
      setNotice('已保存。下一次发起的翻译任务会用这套配置。');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '保存失败');
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setError(null);
    setNotice(null);
    try {
      const result = await testTranslationProvider();
      if (result.ok) {
        setNotice(`连通正常（${result.latencyMs} ms）。${result.message}`);
      } else {
        setError(`测试失败：${result.message}`);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '测试失败');
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card
      title="翻译模型"
      actions={
        <div className="platform-actions">
          <Button variant="ghost" disabled={testing} onClick={() => void test()}>
            {testing ? '测试中…' : '测试连通性'}
          </Button>
          <Button variant="primary" disabled={busy} onClick={() => void save()}>
            {busy ? '保存中…' : '保存'}
          </Button>
        </div>
      }
    >
      {error ? <p className="error">{error}</p> : null}
      {notice ? <p className="notice notice--ok">{notice}</p> : null}

      {!view ? (
        <p className="muted">加载中…</p>
      ) : (
        <>
          <div className="form-grid">
            <label className="field-block">
              <span>提供方</span>
              <select
                className="field"
                value={form.provider ?? view.provider}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    provider: e.target.value as UpdateTranslationProviderInput['provider'],
                  }))
                }
              >
                <option value="openai-compatible">OpenAI 兼容协议（DeepSeek / Moonshot / 自建）</option>
                <option value="mock">mock（本地假翻译，不花钱）</option>
              </select>
            </label>

            <label className="field-block">
              <span>模型</span>
              <input
                className="field"
                value={form.model ?? ''}
                placeholder="deepseek-chat"
                onChange={(e) => setForm((prev) => ({ ...prev, model: e.target.value }))}
              />
            </label>

            <label className="field-block field-block--wide">
              <span>接口地址</span>
              <input
                className="field"
                value={form.baseUrl ?? ''}
                placeholder="https://api.deepseek.com/v1"
                onChange={(e) => setForm((prev) => ({ ...prev, baseUrl: e.target.value }))}
              />
            </label>

            <label className="field-block field-block--wide">
              <span>
                API Key
                {view.apiKeyHint ? (
                  <span className="muted">（已配置，末四位 {view.apiKeyHint}；留空表示不改）</span>
                ) : view.envFallback ? (
                  <span className="muted">（数据库里还没有，当前用环境变量里那一份兜底）</span>
                ) : (
                  <span className="muted">（还没有配置，翻译会直接报错）</span>
                )}
              </span>
              <input
                className="field"
                type="password"
                autoComplete="off"
                value={apiKey}
                placeholder={view.apiKeyHint ? '留空 = 不修改' : 'sk-…'}
                onChange={(e) => setApiKey(e.target.value)}
              />
            </label>

            <label className="field-block">
              <span>每次请求的段数</span>
              <input
                className="field"
                type="number"
                min={1}
                max={20}
                value={form.batchSize ?? view.batchSize}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, batchSize: Number(e.target.value) || 1 }))
                }
              />
            </label>

            <label className="field-block">
              <span>temperature</span>
              <input
                className="field"
                type="number"
                min={0}
                max={1}
                step={0.1}
                value={form.temperature ?? view.temperature}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, temperature: Number(e.target.value) || 0 }))
                }
              />
            </label>
          </div>

          <p className="muted">
            段数越大越快、也越省钱（一次请求翻更多），但单次失败要重试的段落也更多。
            20 段是上限。temperature 建议 0–0.3：翻译要可复现，校对过的那一版不能每次都变。
          </p>
          <p className="muted">
            测试用的是**已保存**的配置 —— 改完先保存，再点测试。
            {view.updatedAt ? ` 当前配置保存于 ${formatDateTime(view.updatedAt)}。` : ''}
          </p>
        </>
      )}
    </Card>
  );
}

/**
 * 图像模型配置（仅平台超管）。
 *
 * 与翻译那份同构，理由也一样：一份 Key 服务全平台，换模型/换 Key 不该走一次
 * 发布 —— 图像模型的价格与可用性变得比文本模型还快。
 *
 * 两处刻意的设计：
 *
 * · **mock 是默认值，而且可以长期留着。** 没有 Key 时客户也能点出效果，
 *   这是上线前唯一能把「建任务 → 生成 → 入库 → 合成」整条链路跑通的路径。
 *   真正接上模型之前，它就是演示模式。
 * · **测试只验凭据，不真出图。** 一张图要花钱，而"连不连得上"用一次
 *   GET /models 就能回答。这条边界必须写在界面上，否则点过绿的人会以为
 *   一定能出图，然后被"没有该模型权限"打回来。
 */
function BrandImageProviderCard() {
  const [view, setView] = useState<BrandImageConfigView | null>(null);
  const [form, setForm] = useState<UpdateBrandImageConfigInput>({});
  const [apiKey, setApiKey] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    try {
      const next = await fetchBrandImageConfig();
      setView(next);
      setForm({ provider: next.provider, baseUrl: next.baseUrl, model: next.model });
      setError(null);
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 403
          ? '这个配置只有平台超管能改。'
          : err instanceof ApiError
            ? err.message
            : '读取图像模型配置失败',
      );
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      // 留空 = 不改（与翻译那份一致）；要清除 Key 请选回演示模式
      const input: UpdateBrandImageConfigInput = { ...form };
      if (apiKey.trim() !== '') input.apiKey = apiKey.trim();
      const next = await saveBrandImageConfig(input);
      setView(next);
      setApiKey('');
      setNotice(
        next.configured
          ? '已保存。客户下次点「AI 图形」就用这套配置。'
          : '已保存，但还没有可用的 Key —— 客户那边会看到「暂不可用」。',
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '保存失败');
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setError(null);
    setNotice(null);
    try {
      const result = await testBrandImageConfig();
      if (result.ok) setNotice(`连通正常（${result.latencyMs} ms）。${result.message}`);
      else setError(`测试失败：${result.message}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '测试失败');
    } finally {
      setTesting(false);
    }
  };

  const provider = form.provider ?? view?.provider ?? 'mock';

  return (
    <Card
      title="图像模型（品牌 AI 图形）"
      actions={
        <div className="platform-actions">
          <Button variant="ghost" disabled={testing} onClick={() => void test()}>
            {testing ? '测试中…' : '测试连通性'}
          </Button>
          <Button disabled={busy} onClick={() => void save()}>
            {busy ? '保存中…' : '保存'}
          </Button>
        </div>
      }
    >
      {error ? <p className="error">{error}</p> : null}
      {notice ? <p className="notice notice--ok">{notice}</p> : null}

      {!view ? (
        <p className="muted">加载中…</p>
      ) : (
        <>
          <div className="form-grid">
            <label className="field-block">
              <span>提供方</span>
              <select
                className="field"
                value={provider}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    provider: e.target.value as UpdateBrandImageConfigInput['provider'],
                  }))
                }
              >
                <option value="mock">演示模式（本地画图形，零成本）</option>
                <option value="openai-images">OpenAI 兼容的 /images/generations</option>
              </select>
            </label>

            <label className="field-block">
              <span>模型</span>
              <input
                className="field"
                value={form.model ?? view.model}
                onChange={(e) => setForm((prev) => ({ ...prev, model: e.target.value }))}
                placeholder={
                  (form.baseUrl ?? view.baseUrl).includes('volces.com')
                    ? 'doubao-seedream-5-0-flash-260915'
                    : DEFAULT_BRAND_ART_MODEL
                }
              />
            </label>
          </div>

          {provider === 'openai-images' ? (
            <>
              <div className="brandpresets">
                {BRAND_ART_PRESETS.map((preset) => {
                  const active =
                    (form.baseUrl ?? view.baseUrl) === preset.baseUrl &&
                    (form.model ?? view.model) === preset.model;
                  return (
                    <button
                      key={preset.key}
                      type="button"
                      className={`brandpresets__item${active ? ' is-active' : ''}`}
                      onClick={() =>
                        setForm((prev) => ({
                          ...prev,
                          baseUrl: preset.baseUrl,
                          model: preset.model,
                        }))
                      }
                    >
                      <span className="brandpresets__name">{preset.label}</span>
                      <span className="brandpresets__price">{preset.price}</span>
                      <span className="brandpresets__note">{preset.note}</span>
                    </button>
                  );
                })}
              </div>
              <p className="muted">
                点一个就是把这家的地址和模型名一起填好。模型名会死 —— `gpt-image-1`
                已经在 2026-10 下线了，报 404/400 时先来这里点一下最新的一档。
                <br />
                火山方舟的模型名<strong>必须带日期后缀</strong>：只写 `doubao-seedream-5-0-flash`
                会返回 model not found，要从模型广场复制完整那一串。
              </p>
            </>
          ) : null}

          <div className="form-grid">
            <label className="field-block">
              <span>接口地址</span>
              <input
                className="field"
                value={form.baseUrl ?? view.baseUrl}
                onChange={(e) => setForm((prev) => ({ ...prev, baseUrl: e.target.value }))}
                placeholder="https://api.openai.com/v1"
              />
            </label>

            <label className="field-block">
              <span>API Key</span>
              <input
                className="field"
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={view.hasKey ? `已保存 ${view.keyHint ?? '****'}` : '还没填'}
                autoComplete="off"
              />
            </label>
          </div>

          <p className="muted">
            {provider === 'mock'
              ? '演示模式：图形在服务端本地合成，不花钱、不联网，客户点得到效果。'
              : '客户点「生成品牌标」会用这套 Key 出图：默认让模型把公司名一起画进去，' +
                '也可以切到「只画图形」由控制台排名字。' +
                'OpenAI 系要的是透明底，火山方舟那类出白底、交付前我们会把白底抠掉。'}
            {view.hasKey ? '' : ' 没有 Key 时，客户那个面板只有一条「暂不可用」的提示 —— 现在没有本地排版可兜底了。'}
          </p>
          <p className="muted">
            测试只验证 Key 能不能用（一次 GET /models），不会真的生成图片 ——
            出图还要看账号有没有这个模型的权限。
          </p>
        </>
      )}
    </Card>
  );
}
